<template>
  <div class="et-row">
    <template v-for="segment in segments">
      <div
        v-if="segment.type === 'gap'"
        :key="segment.key"
        class="et-row__gap"
        :style="segmentStyle(segment)"
      >
        {{ segment.label }}
      </div>
      <div
        v-else
        :key="segment.key"
        class="et-row__bar-wrap"
        :style="segmentStyle(segment)"
      >
        <EXTooltip
          placement="top"
          :title="formatTooltipTitle(segment.record)"
          :description="formatTooltipDescription(segment.record, todayMs)"
          :delay="300"
          :force-open="showTooltip(segment)"
        >
          <PeriodBar
            class="et-row__bar"
            :state="segment.record.state"
            :label="segment.label"
            :detail="segment.detail"
            :open-ended="segment.openEnded"
            :selectable="isSelectable(segment)"
            :selected="isSelectable(segment) && selectedId === segment.record.id"
            :hovered="isMirroredHover(segment)"
            @select="$emit('select', segment.record.id)"
            @hover="onBarHover(segment, $event)"
          />
        </EXTooltip>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref } from "vue";

import PeriodBar from "@components/PeriodBar/PeriodBar.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import {
  formatTooltipDescription,
  formatTooltipTitle,
} from "./employeeTimelineTooltip";

const props = defineProps({
  segments: { type: Array, default: () => [] },
  selectable: { type: Boolean, default: false },
  selectedId: { type: [String, Number], default: null },
  hoveredId: { type: [String, Number], default: null },
  todayMs: { type: Number, default: null },
});

const emit = defineEmits(["select", "hover"]);

/** Pointer is over this bar (direct timeline hover). */
const pointerBarId = ref(null);

function isSelectable(segment) {
  return props.selectable && segment.record.selectable !== false;
}

function isMirroredHover(segment) {
  if (props.hoveredId === null || props.hoveredId === undefined) return false;
  return String(segment.record.id) === String(props.hoveredId);
}

function showTooltip(segment) {
  if (pointerBarId.value != null) {
    return String(segment.record.id) === String(pointerBarId.value);
  }
  return isMirroredHover(segment);
}

function onBarHover(segment, isHovered) {
  pointerBarId.value = isHovered ? segment.record.id : null;
  emit("hover", segment.record.id, isHovered);
}

function segmentStyle(segment) {
  return {
    position: "absolute",
    left: `${segment.leftPx}px`,
    width: `${segment.widthPx}px`,
  };
}
</script>

<style scoped>
.et-row {
  position: relative;
  width: 100%;
  height: 24px;
}

.et-row__bar-wrap {
  position: absolute;
  top: 0;
  box-sizing: border-box;
  height: 24px;
}

.et-row__bar-wrap :deep(.el-tooltip),
.et-row__bar-wrap :deep(.el-tooltip ._parent-content) {
  display: block;
  width: 100%;
  height: 100%;
}

.et-row__bar {
  position: relative;
  top: 0;
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  height: 24px;
}

.et-row__gap {
  position: absolute;
  top: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 8px;
  border: 1px dashed #d0d5dd;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 400;
  color: #98a2b3;
  text-align: center;
}
</style>
